import { Maybe } from 'dg-web-shared/lib/MaybeV2';
import { Clickable } from 'dg-web-shared/ui/Clickable';
import * as Icons24 from 'dg-web-shared/ui/icons/Icons24';
import { SvgIcon } from 'dg-web-shared/ui/icons/SvgIcon';
import { css } from '@emotion/css';
import { Colors, Typo } from './vars';
import { rgba } from 'utils/src/Rgba.ts';
import React, { type JSX } from 'react';

export interface OnEnterContext {
    blur: () => void;
}

export const Close = (p: { onClick: () => void }): JSX.Element => (
    <Clickable
        element="div"
        className={css({
            right: 0,
            top: 0,
            position: 'absolute',
            width: '24px',
            height: '24px',
        })}
        onClick={p.onClick}
    >
        <SvgIcon width={24} height={24} icon={Icons24.clear_b} />
    </Clickable>
);

interface Props {
    inputRef?: React.RefObject<HTMLInputElement | null>;
    value: string;
    label: string;
    inputType: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onChange: (v: any) => void;
    onBlur?: () => void;
    onFocus?: Maybe<() => void>;
    onClear?: Maybe<() => void>;
    onEnter?: (onEnterContext: OnEnterContext) => void;
    focusOnMount?: boolean;
    disabled?: boolean;
    errorText?: Maybe<string>;
    tabIndex?: Maybe<number>;
}

interface LabelProps {
    label: string;
    focused: boolean;
    hidden?: boolean;
    disabled?: boolean;
}

const Label = (p: LabelProps) => {
    const isHidden = p.hidden ?? false;
    const isDisabled = p.disabled ?? false;
    return (
        <label
            className={css({
                display: 'block',
                ...Typo.label,
                visibility: isHidden ? 'hidden' : 'visible',
                color: getLabelColor(p.focused, isDisabled),
            })}
        >
            <div className={css({ userSelect: 'none' })}>{p.label}</div>
        </label>
    );
};

function getLabelColor(isFocused: boolean, isDisabled: boolean) {
    if (isDisabled) {
        return rgba(Colors.darkBlue, 0.4);
    }
    if (isFocused) {
        return Colors.lightBlue;
    }
    return rgba(Colors.darkBlue, 0.7);
}

interface BelowContentProps {
    underline: boolean;
    focused: boolean;
    errorText: Maybe<string>;
}

export const BelowContent = (p: BelowContentProps): JSX.Element => {
    return (
        <div
            className={css({
                height:
                    p.underline && (p.focused || !!p.errorText)
                        ? '16px'
                        : '17px',
                ...Typo.caption,
                color: p.errorText ? Colors.error : 'transparent',
                borderTop: p.underline
                    ? getUnderlineStyle(p.focused, !!p.errorText)
                    : '0px none white',
            })}
            data-underline={p.underline}
            data-focused={p.focused}
            data-error={!!p.errorText}
        >
            {p.errorText}
        </div>
    );
};

function getUnderlineStyle(isFocused: boolean, isError: boolean) {
    if (isError) {
        return `2px solid ${Colors.error}`;
    }
    if (isFocused) {
        return `2px solid ${Colors.lightBlue}`;
    }
    return `1px solid ${Colors.darkBlue}`;
}

interface ContentProps {
    focused: boolean;
    empty: boolean;
    userSelectable: boolean;
    children?: React.ReactNode;
    disabled?: Maybe<boolean>;
}

export const Content = (p: ContentProps): JSX.Element => {
    const isDisabled = p.disabled ?? false;
    return (
        <div
            className={css({
                ...Typo.robotoRegular,
                paddingTop: '2px',
                color: getContentColor(p.focused, isDisabled),
                position: 'relative',
                userSelect: p.userSelectable ? 'auto' : 'none',
            })}
        >
            {p.children}
        </div>
    );
};

function getContentColor(isFocused: boolean, isDisabled: boolean) {
    if (isDisabled) {
        return rgba(Colors.darkBlue, 0.6);
    }
    if (isFocused) {
        return Colors.lightBlue;
    }
    return Colors.darkBlue;
}

interface LabeledElementProps {
    children?: React.ReactNode;
    onClick?: () => void;
}

export const LabeledElement = (p: LabeledElementProps): JSX.Element => {
    return (
        <Clickable
            element="div"
            className={css({
                position: 'relative',
                zIndex: 0,
                paddingTop: '16px',
                paddingLeft: '16px',
                paddingRight: '16px',
            })}
            onClick={p.onClick}
            disabled={!p.onClick}
        >
            {p.children}
        </Clickable>
    );
};

export function TextField(props: Props) {
    // Callers that need to blur the field imperatively (the enforcement return
    // button) pass their own ref in; otherwise the component keeps its own.
    const localInputRef = React.useRef<HTMLInputElement>(null);
    const inputRef = props.inputRef ?? localInputRef;
    const [focused, setFocused] = React.useState(false);

    function focusInputField(): void {
        inputRef.current?.focus();
    }

    function blurInputField(): void {
        inputRef.current?.blur();
    }

    // Caret position captured while typing, so it can be restored after the
    // value comes back with automatic modifications applied. This replaces the
    // old UNSAFE_componentWillUpdate, which could inspect the DOM before React
    // wrote the new value; an effect runs after that write, so the pre-change
    // length and caret index have to be recorded here instead.
    const pendingCaret = React.useRef<{ length: number; index: number } | null>(
        null,
    );

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function _onChange(e: any): void {
        const node = e.target;
        pendingCaret.current = {
            length: node.value.length,
            index: node.selectionStart ?? 0,
        };
        props.onChange(node.value);
    }

    React.useLayoutEffect(() => {
        const pending = pendingCaret.current;
        pendingCaret.current = null;
        if (!pending) {
            return;
        }
        const node = inputRef.current;
        if (!node || props.value === undefined || props.value === null) {
            return;
        }
        const newIdx = Math.max(
            0,
            node.value.length - pending.length + pending.index,
        );
        node.selectionStart = newIdx;
        node.selectionEnd = newIdx;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.value]);

    React.useEffect(() => {
        if (props.focusOnMount) {
            focusInputField();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function hasValue(): boolean {
        return !!props.value;
    }

    function renderHint(): JSX.Element | null {
        if (hasValue()) {
            return null;
        } else {
            return (
                <div
                    className={css({
                        zIndex: -1,
                        width: '100%',
                        position: 'absolute',
                        ...Typo.robotoLight,
                        fontStyle: 'italic',
                        fontSize: '20px',
                        color: rgba(Colors.darkBlue, 1),
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                    })}
                >
                    {props.label}
                </div>
            );
        }
    }

    return (
        <LabeledElement>
            <Label focused={focused} hidden={!hasValue()} label={props.label} />
            <Content userSelectable={true} empty={false} focused={focused}>
                {renderHint()}
                <input
                    className={css({
                        margin: 0,
                        padding: 0,
                        border: 'none',
                        outline: 'none',
                        verticalAlign: 'baseline',
                        whiteSpace: 'normal',
                        background: 'none',
                        lineHeight: 'inherit',
                        fontFamily: 'inherit',
                        fontSize: '20px',
                        color: 'inherit',
                        fontWeight: 'inherit',
                        display: 'block',
                        width: '100%',
                        '&:focus': {
                            outline: 0,
                        },
                    })}
                    ref={inputRef}
                    autoCapitalize="none"
                    autoComplete="off"
                    value={props.value}
                    type={props.inputType}
                    onFocus={(): void => {
                        if (props.onFocus) {
                            props.onFocus();
                        }
                        setFocused(true);
                    }}
                    onBlur={() => {
                        setFocused(false);

                        if (props.onBlur) {
                            props.onBlur();
                        }
                    }}
                    onChange={(
                        e: React.SyntheticEvent<HTMLInputElement>,
                    ): void => _onChange(e)}
                    onKeyDown={(
                        e: React.KeyboardEvent<HTMLInputElement>,
                    ): void => {
                        if (props.onEnter && e.key === 'Enter') {
                            props.onEnter({
                                blur: () => blurInputField(),
                            });
                        }
                    }}
                    disabled={props.disabled}
                    tabIndex={props.tabIndex || undefined}
                />
                {props.onClear && props.inputType !== 'search' && (
                    <Close onClick={props.onClear} />
                )}
            </Content>
            <BelowContent
                focused={focused}
                underline={true}
                errorText={props.errorText}
            />
        </LabeledElement>
    );
}
